import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components';
import ListTitles from './v2/ListTitles';
import { ReactComponent as ArrowBack } from "../assets/svg/arrow-back.svg";
import Courses from './Courses';
import { useCourses } from '../hooks/v2/useCourses';
import { useEpisodes } from '../hooks/v2/useEpisodes';
import { useChapters } from '../hooks/v2/useChapters';
import { useLessons } from '../hooks/v2/useLessons';
import { useFree } from '../hooks/v2/useFree';
import { useCompleted } from '../hooks/v2/useCompleted';
import { usePurchases } from '../hooks/v2/usePurchases';
import LessonViewMiddleware from './admin/LessonViewMiddleware';

const Tree = (props: ITreeProps) => {
  const { courses, coursesArray } = useCourses();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>(props.payload.courseId);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | undefined>(props.payload.episodeId);
  const [selectedChapterId, setSelectedChapterId] = useState<string | undefined>(props.payload.chapterId);
  const [selectedLessonId, setSelectedLessonId] = useState<string | undefined>(props.payload.lessonId);

  const { episodes, episodesArray } = useEpisodes({ courseId: selectedCourseId });
  const { chapters, chaptersArray } = useChapters({ episodeId: selectedEpisodeId });
  const { lessons, lessonsArray } = useLessons({ chapterId: selectedChapterId })

  const { completed, completedArray } = useCompleted({ userId: props.payload.userId, episodeId: selectedEpisodeId, chapterId: selectedChapterId }); // sync
  const { purchases, purchasesArray } = usePurchases({ userId: props.payload.userId }); // only episodes
  const { free, freeArray } = useFree(); // sync

  const GetNextEpisodeUncompletedAvailable = useCallback((array: typeof episodesArray) => {

    let firstUncompletedFreeEpisode = undefined;
    for (let index = 0; index < array.length; index++) {
      const episode = array[index];
      if (completed[episode.id] === undefined) 
        if (purchases[episode.id] === undefined) {
          if (free[episode.id] === true)
            if (firstUncompletedFreeEpisode !== undefined) firstUncompletedFreeEpisode = episode;
        } else return episode;
    }

    if (firstUncompletedFreeEpisode !== undefined) return firstUncompletedFreeEpisode;
    return null;
  }, [purchases, free, completed]);

  const GetNextChapterUncompletedAvailable = useCallback((array: typeof chaptersArray) => {
    if(selectedEpisodeId === undefined) throw "No está definido el id del episode";

    for (let index = 0; index < array.length; index++) {
      const chapter = array[index];
      if (completed[chapter.id] === undefined)
        if (purchases[selectedEpisodeId] === undefined) {
          if (free[chapter.id] === true) return chapter;
        } else return chapter;
    }

    return null;
  }, [selectedEpisodeId, purchases, free, completed]);

  const GetNextLessonUncompletedAvailable = useCallback((array: typeof lessonsArray) => {
    if (selectedEpisodeId === undefined) throw "No está definido el id del episode";

    for (let index = 0; index < array.length; index++) {
      const lesson = array[index];
      if (completed[lesson.id] === undefined)
        if (purchases[selectedEpisodeId] === undefined) {
          if (free[lesson.id] === true) return lesson;
        } else return lesson;
    }

    return null;
  }, [selectedEpisodeId, purchases, free, completed]);

  const GetItemProperties = useCallback((props: {
    isFree: boolean;
    isPurchased: boolean;
    isCompleted: boolean;
    itemId: string;
    nextItemId: string | null;
  }) => {
    const isNextItem = props.nextItemId !== null && props.nextItemId === props.itemId;
    return {
      isFree: props.isFree,
      isPurchased: props.isPurchased,
      isCompleted: props.isCompleted,
      isEnabled: (props.isPurchased && props.isCompleted) || (props.isPurchased && isNextItem) || (props.isPurchased === false && props.isFree)
    };

  }, []);

  const treeCourses = useMemo(() => {
    return (
      <Wrapper>
        <TitleWrapper>
          <Title>Cursos</Title>
        </TitleWrapper>
        <Courses
          data={coursesArray}
          getIsAllowed={(payload: IDBRealtimeCourse) => {
            const courseId = payload.id;
            let course = courses[courseId];
            if (course !== undefined && course.episodes !== undefined) {
              const episodesIds = Object.keys(course.episodes);

              for (let index = 0; index < episodesIds.length; index++) {
                const episodeId = episodesIds[index];
                if (course.episodes[episodeId] === true) {
                  if (purchases[episodeId] === true || free[episodeId] === true) {
                    return true;
                  }
                }
              }
            }
            return false;
          }}
          onClick={(course: IDBRealtimeCourse) => setSelectedCourseId(course.id)}
          className={{}}
        />
      </Wrapper>
    );
  }, [courses, coursesArray, purchases, free]);

  const treeEpisodes = useMemo(() => {
    if (selectedCourseId === undefined) return;
    const course = courses[selectedCourseId];
    if (course === undefined) return;

    const nextEpisode = GetNextEpisodeUncompletedAvailable(episodesArray);

    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedCourseId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{course.title}</Title>
        </TitleWrapper>
        <ListTitles
          items={episodesArray.map(episode => {
            const properties = GetItemProperties({
              itemId: episode.id,
              nextItemId: nextEpisode === null ? null : nextEpisode.id,
              isFree: free[episode.id] === true,
              isPurchased: purchases[episode.id] === true,
              isCompleted: completed[episode.id] === true
            });
            return {
              id: episode.id,
              label: episode.title,
              ...properties
            };
          })}
          onClick={(episode) => setSelectedEpisodeId(episode.id)}
        />
      </Wrapper>
    );
  }, [
    selectedCourseId,
    courses,
    episodesArray,
    purchases,
    free,
    completed,
    GetNextEpisodeUncompletedAvailable,
    GetItemProperties
  ]);

  const treeChapters = useMemo(() => {
    if (selectedEpisodeId === undefined) return;
    const episode = episodes[selectedEpisodeId];
    if (episode === undefined) return;

    const nextChapter = GetNextChapterUncompletedAvailable(chaptersArray);

    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedEpisodeId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{episode.title}</Title>
        </TitleWrapper>
        <ListTitles
          items={chaptersArray.map(chapter => {
            const properties = GetItemProperties({
              itemId: chapter.id,
              nextItemId: nextChapter === null ? null : nextChapter.id,
              isFree: free[chapter.id] === true,
              isPurchased: purchases[selectedEpisodeId] === true,
              isCompleted: completed[chapter.id] === true
            });
            return {
              id: chapter.id,
              label: chapter.title,
              ...properties
            };
          })}
          onClick={(chapter) => setSelectedChapterId(chapter.id)}
        />
      </Wrapper>
    );
  }, [
    selectedEpisodeId,
    episodes,
    chaptersArray,
    purchases,
    free,
    completed,
    GetNextChapterUncompletedAvailable,
    GetItemProperties
  ]);

  const treeLessons = useMemo(() => {
    if (selectedChapterId === undefined || selectedEpisodeId === undefined) return;
    const chapter = chapters[selectedChapterId];
    if (chapter === undefined) return;

    const nextLesson = GetNextLessonUncompletedAvailable(lessonsArray);

    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedChapterId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{chapter.title}</Title>
        </TitleWrapper>
        <ListTitles
          items={lessonsArray.map(lesson => {
            const properties = GetItemProperties({
              itemId: lesson.id,
              nextItemId: nextLesson === null ? null : nextLesson.id,
              isFree: free[lesson.id] === true,
              isPurchased: purchases[selectedEpisodeId] === true,
              isCompleted: completed[lesson.id] === true
            });
            return {
              id: lesson.id,
              label: lesson.title,
              ...properties
            };
          })}
          onClick={(lesson) => setSelectedLessonId(lesson.id)}
        />
      </Wrapper>
    );
  }, [
    selectedChapterId,
    selectedEpisodeId,
    chapters,
    lessonsArray,
    purchases,
    free,
    completed,
    GetNextLessonUncompletedAvailable,
    GetItemProperties
  ]);

  const lessonDisplay = useMemo(() => {
    if (
      selectedLessonId === undefined ||
      selectedEpisodeId === undefined ||
      selectedChapterId === undefined ||
      selectedCourseId === undefined
    ) return;

    const lesson = lessons[selectedLessonId];
    if (lesson === undefined) return;

    const lessonNext = GetNextLessonUncompletedAvailable(lessonsArray);
    
    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedLessonId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{lesson.title}</Title>
        </TitleWrapper>
        <LessonViewMiddleware
          id={lesson.id}
          chapterId={selectedChapterId}
          episodeId={selectedEpisodeId}
          courseId={selectedCourseId}
          userId={props.payload.userId}
        />
      </Wrapper>
    );
  }, [
    selectedLessonId,
    selectedChapterId,
    selectedEpisodeId,
    selectedCourseId,
    lessonsArray,
    lessons,
    completed,
    GetNextLessonUncompletedAvailable
  ]);

  const stage = useMemo(() => {
    if (selectedLessonId !== undefined) return 'SHOW_LESSON';
    else if (selectedChapterId !== undefined) return 'SHOW_LESSONS';
    else if (selectedEpisodeId !== undefined) return 'SHOW_CHAPTERS';
    else if (selectedCourseId !== undefined) return 'SHOW_EPISODES';
    else return 'SHOW_COURSES';
  }, [selectedChapterId, selectedCourseId, selectedEpisodeId, selectedLessonId]);

  let names = [];
  if(props.payload.userLastname !== undefined)names.push(props.payload.userLastname);
  if(props.payload.userName !== undefined)names.push(props.payload.userName);
  return (
    <>
      <ContainerWrapper>
        {names.length > 0 && <Tag>{`${names.join(', ')}`}</Tag>}
        {names.length === 0 && <TagNoIdentifier>Usuario no identificado</TagNoIdentifier>}
        <Container>
          {isLoading === true && <Spinner src="loading-blue.gif" />}
          {isLoading === false && (
            <>
              {stage === "SHOW_COURSES" && treeCourses}
              {stage === "SHOW_EPISODES" && treeEpisodes}
              {stage === "SHOW_CHAPTERS" && treeChapters}
              {stage === "SHOW_LESSONS" && treeLessons}
              {stage === "SHOW_LESSON" && lessonDisplay}
            </>
          )}
        </Container>
      </ContainerWrapper>
    </>);
}

export default Tree;


const Tag = styled.div`
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px 10px;
  margin-bottom: 10px;
  font-size: 0.9em;
  font-weight: 600;
  margin: 0px 0px 25px 0px;
  text-transform: capitalize;
`;

const TagNoIdentifier = styled.div`
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px 10px;
  margin-bottom: 10px;
  font-size: 0.9em;
  font-weight: 600;
  margin: 0px 0px 25px 0px;
`;


const ContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #32b0ea;
  padding: 20px 15px;
  box-sizing: border-box;
`;

const Container = styled.div`
  max-width: 450px;
  width: 100%;
`;

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: 5px 0px;
  margin-bottom: 25px;
  cursor: pointer;

  user-select: none;
`;

const Back = styled.div`
  fill: white;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  margin-right: 15px;
`;

const Title = styled.div`
  font-size: 1.5em;
  font-weight: 600;
  color: white;
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;
